import { Inject, Injectable, NgZone } from '@angular/core';
import { HttpBackend, HttpClient, HttpHeaders, HttpRequest, HttpResponse } from '@angular/common/http';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';

// RxJs
import {
  BehaviorSubject,
  catchError,
  filter, finalize,
  forkJoin, from,
  Observable,
  of,
  Subject, switchMap,
  take,
  takeUntil,
  tap,
  throwError
} from 'rxjs';
import { map } from 'rxjs/operators';

// NgRX
import { Store } from '@ngrx/store';
import { AppState } from '@core/store/reducers';
import { SessionActions, SessionEffectsActions } from '@core/store/actions/session.actions';

// ENVs
import { environment } from '@environments/environment';

// Providers
import { UsersService } from '@core/providers/services/maintainer/users/users.service';

// Models
import { User } from '@core/models/session.interface';
import { UserStatus } from '@core/models/maintainer.interface';
import { KeycloakService } from './keycloak/keycloak.service';



@Injectable({
  providedIn: 'root'
})
export class SecurityService {

  public account!: User;
  public showIframeLogout!: boolean;

  private _OA2T!: any;
  private $token!: string;
  private logoutTimeout!: NodeJS.Timeout;
  private $thirtyMinutes: number;
  private getOA2TTimeout!: number;
  private logoutTimestamp!: number;


  public readonly authenticationStatus$ = new BehaviorSubject<EventType>(EventType.INITIALIZE_START);

  private readonly _destroying$ = new Subject<void>();


  constructor(
    private usersService: UsersService,
    private keycloakService: KeycloakService,
    private readonly ngZone: NgZone,
    private readonly router: Router,
    private readonly httpClient: HttpClient,
    private readonly httpBackend: HttpBackend,
    private readonly store: Store<AppState>,
  ) {
    // TO-DO: LOADING SERVICE
    this.$thirtyMinutes = environment.OAUTH2.LOGOUT_COUNTDOWN_TIME_MINUTES * 30 * 1000;
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }


  public fetchRentalUser(): void {
    this.authenticationStatus$.next(EventType.LOGIN_START);
    console.log({ token: this.token });
    this.getOA2T(this.keycloakService.getToken()).pipe(
      switchMap(res => {
        console.log({ res });
        this.token = res?.token;
        // this.$refreshToken = res?.refresh_token;
        return this.usersService.getUser(this.keycloakService.getUserInfo()?.username).pipe(
          tap((res) => {
            console.log({ res });
            this.initializeSessionCountdown();
            this.authenticationStatus$.next(EventType.LOGIN_SUCCESS);
          })
        );
      }),
      catchError(err => {
        localStorage.setItem('error-gateway', 'error: ' + JSON.stringify(err));

        this.showIframeLogout = true;
        this.cleanStorage();
        this.authenticationStatus$.next(EventType.LOGIN_FAILURE);
        this.router.navigateByUrl('401');
        return throwError(err);
      })
    ).subscribe((user: any) => {
      console.log({ user });
      if (!!user?.email && user.status === UserStatus.ON) {

        const { email, name, center, role, country, status } = user;
        this.account = { email, fullName: name, center, role, country, status };

        this.injectSession();

      } else { this.router.navigateByUrl('401'); }
    });
  }


  private injectSession(): void {
    console.log({ account: this.account });
    if (!!this.account?.email) {
      this.store.dispatch(SessionActions.injectUser({
        user: this.account
        // {
        //   fullName: String(this.account.fullName),
        //   email: this.account.email,
        //   country: this.account.country || 'CL',
        //   center: this.account.center as any,
        //   role: this.account.role,
        //   status: this.account.status
        // }
      }));

      this.initializeSessionCountdown();
    } else { return; }
  }


  private initializeSessionCountdown(): void {
    // this.loadingService.hide();
    this.logoutTimestamp = performance.now() + this.$thirtyMinutes;

    this.ngZone.runOutsideAngular(() => this.logoutTimeout = setTimeout(() => this.ngZone.run(() => this.logOut()), this.$thirtyMinutes));
  }


  private handleVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      if (performance.now() >= this.logoutTimestamp) {
        clearTimeout(this.logoutTimeout);
        this.logOut();
      }
    }
  }


  public isAuthenticated(next: ActivatedRouteSnapshot, state: RouterStateSnapshot, typeCanActivate: string): Observable<any> {
    console.log({ keycloak: this.keycloakService.getUserInfo(), token: this.keycloakService.getToken() });
    return of(!!this.keycloakService.getUserInfo())
  }


  private cleanStorage(): void {
    localStorage.clear();
    sessionStorage.clear();
    // TO-DO: ACTION delete User;
  }


  public getOA2T(token: string = ''): Observable<any> {
    if (this.getOA2TTimeout > (new Date()).getTime()) return of(this._OA2T);

    const headers = new HttpHeaders({
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${ token }`,
    });

    // return this.httpBackend.handle(new HttpRequest<any>('GET', `${ 'http://localhost:8080/token/exchange' }`, { headers }
    return this.httpBackend.handle(new HttpRequest<any>('GET', `${ environment.URI }${ 'rental-sap/token/exchange'/*environment.OAUTH2.TOKEN_URL*/ }`, { headers }
    )).pipe(
      filter(res => res instanceof HttpResponse),
      map(({ body }: any) => body),
      tap((body) => {
        this._OA2T = { token: body?.access_token };
        this.getOA2TTimeout = (new Date()).getTime() + body?.expires_in * 1000;
      })
    );
  }


  public restartLogoutTimeout(): void {
    if (this.logoutTimeout && this.account) {
      clearTimeout(this.logoutTimeout);
      this.initializeSessionCountdown();
    }
  }


  public logIn(): Observable<any> {
    return from(this.keycloakService.init()).pipe(finalize(() => this.injectSession()));
  }


  public logOut(): Observable<any> {
    this.cleanStorage();
    this.authenticationStatus$.next(EventType.LOGOUT_START);

    return from(this.keycloakService.logout()).pipe(
      tap(() => {
        this.authenticationStatus$.next(EventType.LOGOUT_SUCCESS);
        this.finishSubscriptions();
      }),
      catchError(err => {
        this.authenticationStatus$.next(EventType.LOGOUT_FAILURE);
        console.error({ err });
        return throwError(() => err);
      })
    );
  }


  private finishSubscriptions(): void {
    // this.$stopTokenRefresh();
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  // private $startTokenRefresh(): void {
  //   // Se ejecuta cada 45 minutos
  //   this.$refreshInterval = setInterval(() => {
  //     this.$refreshAuth(this.$refreshToken).pipe(
  //       catchError(err => throwError(err))
  //     ).subscribe((res) => {
  //       console.log('Token renovado:', res);
  //       this.token = res?.token;
  //       this.$refreshToken = res?.refresh_token;
  //     });
  //   }, 45 * 60 * 1000); // (2700000 ms)
  // }
  //
  //
  // private $stopTokenRefresh(): void {
  //   if (this.$refreshInterval) {
  //     clearInterval(this.$refreshInterval);
  //   }
  // }


  public get token(): string {
    return this.$token;
  }

  private set token(newToken: string) {
    this.$token = newToken;
  }
}



export enum EventType {
  LOGOUT_START = 'LOGOUT_START',
  LOGOUT_SUCCESS = 'LOGOUT_SUCCESS',
  LOGOUT_FAILURE = 'LOGOUT_FAILURE',
  INITIALIZE_START = 'INITIALIZE_START',
  LOGIN_START = 'LOGIN_START',
  LOGIN_SUCCESS = 'LOGIN_SUCCESS',
  LOGIN_FAILURE = 'LOGIN_FAILURE'
}
